import { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useOverlayScrollLock } from './useOverlayScrollLock';
import { ChevronLeft, X } from 'lucide-react';

interface ModalProps {
    children: ReactNode;
    title: string;
    subTitle?: ReactNode;
    onClose: () => void;
    transparent?: boolean;
    desktopClassName?: string;
    panelClassName?: string;
}

export const Modal = ({
    title,
    subTitle,
    children,
    onClose,
    transparent = true,
    desktopClassName = '',
    panelClassName = '',
}: ModalProps) => {
    const [isMobile, setIsMobile] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [condensed, setCondensed] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    useOverlayScrollLock(scrollRef);
    const [mobileViewport, setMobileViewport] = useState({ height: 0, offsetTop: 0 });

    useEffect(() => {
        const media = window.matchMedia('(max-width: 640px)');
        const updateScreenMode = () => {
            setIsMobile(media.matches);
        };

        updateScreenMode();
        media.addEventListener('change', updateScreenMode);

        return () => {
            media.removeEventListener('change', updateScreenMode);
        };
    }, []);

    useEffect(() => {
        const frame = window.requestAnimationFrame(() => setIsOpen(true));
        return () => window.cancelAnimationFrame(frame);
    }, []);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);

    useEffect(() => {
        if (!isMobile) {
            setMobileViewport({ height: 0, offsetTop: 0 });
            return;
        }

        const viewport = window.visualViewport;
        const updateMobileViewport = () => {
            const height = viewport?.height || window.innerHeight;
            const offsetTop = viewport?.offsetTop || 0;

            setMobileViewport((current) => {
                if (
                    Math.abs(current.height - height) < 1 &&
                    Math.abs(current.offsetTop - offsetTop) < 1
                ) {
                    return current;
                }

                return { height, offsetTop };
            });
        };

        updateMobileViewport();
        window.addEventListener('resize', updateMobileViewport);
        viewport?.addEventListener('resize', updateMobileViewport);
        viewport?.addEventListener('scroll', updateMobileViewport);

        return () => {
            window.removeEventListener('resize', updateMobileViewport);
            viewport?.removeEventListener('resize', updateMobileViewport);
            viewport?.removeEventListener('scroll', updateMobileViewport);
        };
    }, [isMobile]);

    const portalRoot = typeof document !== 'undefined' ? document.getElementById('outside-root') : null;
    if (!portalRoot) {
        return null;
    }

    const mobileViewportStyle: CSSProperties | undefined = isMobile && mobileViewport.height
        ? {
            bottom: 'auto',
            height: `${mobileViewport.height}px`,
            top: `${mobileViewport.offsetTop}px`,
        }
        : undefined;

    const mobilePanelStyle: CSSProperties | undefined = isMobile && mobileViewport.height
        ? {
            height: '100%',
            maxHeight: '100%',
        }
        : undefined;

    return createPortal(
        <div
            className="fixed inset-0 z-[1000] flex items-start justify-center overflow-hidden p-0 sm:p-6"
            role="dialog"
            aria-modal="true"
            style={mobileViewportStyle}
        >
            <div
                className={`absolute inset-0 transition-opacity duration-200 ${isOpen ? 'opacity-100' : 'opacity-0'}`}
                onClick={onClose}
                aria-hidden="true"
                style={{
                    background: transparent ? 'var(--sf-overlay-backdrop)' : 'rgb(0 0 0 / 0.38)',
                    backdropFilter: transparent ? 'blur(10px)' : 'blur(6px)',
                }}
            />

            <div
                className={`relative z-[1] flex h-full min-h-0 w-full justify-center transition-all duration-200 ${
                    isMobile ? 'items-end' : 'items-center'
                } ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}
                onClick={onClose}
            >
                <div
                    className={`sf-overlay-panel ${panelClassName} flex min-h-0 w-full flex-col overflow-hidden ${
                        isMobile
                            ? 'h-[100dvh] max-h-[100dvh] rounded-none'
                            : `max-h-[calc(100dvh-3rem)] max-w-[min(92vw,56rem)] rounded-[1.75rem] ${desktopClassName}`.trim()
                    }`}
                    style={mobilePanelStyle}
                    onClick={(event) => event.stopPropagation()}
                >
                    {/* O cabecalho fica dentro da area de rolagem: o conteudo passa por tras dele e aparece desfocado. */}
                    <div
                        ref={scrollRef}
                        data-overlay-scroll
                        className="min-h-0 flex-1 overflow-y-auto overscroll-contain [overflow-anchor:none]"
                        onScroll={(event) => setCondensed(event.currentTarget.scrollTop > 8)}
                    >
                        <div className="sf-modal-header sticky top-0 z-20 grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 px-4 py-3 sm:px-6 sm:py-4">
                            <button
                                type="button"
                                className="sf-icon-button sf-close-button btn btn-circle btn-sm shrink-0"
                                onClick={onClose}
                                aria-label={isMobile ? 'Voltar' : 'Fechar'}
                            >
                                {isMobile ? <ChevronLeft aria-hidden="true" /> : <X aria-hidden="true" />}
                            </button>

                            <h2 className={`sf-overlay-title min-w-0 ${condensed ? 'is-condensed' : ''}`}>
                                {title}
                            </h2>
                            {subTitle && <div className="col-start-2 mt-0.5 text-sm text-base-content/65">{subTitle}</div>}
                        </div>

                        <div className="px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4 sm:px-6 sm:pb-6 sm:pt-5">
                            {children}
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        portalRoot,
    );
};
